/**
 * First-time PIN setup: enter twice, then encrypt PIN layer + unlock session.
 * Legacy wallets (old password encryption) need wallet password once before PIN.
 */

import { useEffect, useState } from 'react'
import { accountManager } from '@properwallet/wallet-core'
import { PIN_LENGTH } from '../components/PinInput'
import PinInput from '../components/PinInput'
import { useWalletStore } from '../store/wallet.store'
import { useTranslation } from '../hooks/useTranslation'
import Logo from '../components/Logo'

type Step = 'enter' | 'confirm'

export default function SetupPinScreen() {
  const { t } = useTranslation()
  const {
    activeAccount,
    setupPin,
    unlockWithPin,
    navigate,
    pendingLegacyWalletPassword,
    setPendingLegacyWalletPassword,
  } = useWalletStore()
  const [legacyReady, setLegacyReady] = useState(false)
  const [needsLegacyPassword, setNeedsLegacyPassword] = useState(false)
  const [localPassword, setLocalPassword] = useState('')
  const [passwordGateDone, setPasswordGateDone] = useState(false)
  const [step, setStep] = useState<Step>('enter')
  const [firstPin, setFirstPin] = useState('')
  const [confirm, setConfirm] = useState('')
  const [error, setError] = useState('')
  const [busy, setBusy] = useState(false)

  useEffect(() => {
    void accountManager.init().then(() => {
      const need = accountManager.needsLegacyWalletPasswordForPinSetup()
      setNeedsLegacyPassword(need)
      if (!need || pendingLegacyWalletPassword) {
        setPasswordGateDone(true)
      }
      setLegacyReady(true)
    })
  }, [pendingLegacyWalletPassword])

  if (!legacyReady) {
    return (
      <div className="flex flex-col h-full bg-surface pt-safe items-center justify-center">
        <div className="w-8 h-8 border-2 border-brand border-t-transparent rounded-full animate-spin" />
      </div>
    )
  }

  const verifyPasswordGate = async () => {
    const name = activeAccount?.name
    if (!name || !localPassword.trim()) {
      setError(t('common.error'))
      return
    }
    setBusy(true)
    setError('')
    try {
      const ok = await accountManager.checkPassword(name, localPassword)
      if (!ok) {
        setError(t('onboarding.importError'))
        return
      }
      setPendingLegacyWalletPassword(localPassword)
      setPasswordGateDone(true)
    } catch (e) {
      setError(e instanceof Error ? e.message : t('common.error'))
    } finally {
      setBusy(false)
    }
  }

  const onFirstChange = (v: string) => {
    setError('')
    setFirstPin(v)
    if (v.length === PIN_LENGTH) {
      setStep('confirm')
      setConfirm('')
    }
  }

  const onConfirmChange = (v: string) => {
    setError('')
    setConfirm(v)
    if (v.length === PIN_LENGTH) {
      void submitPair(firstPin, v)
    }
  }

  const submitPair = async (a: string, b: string) => {
    if (a !== b) {
      setError(t('pin.wrong'))
      setStep('enter')
      setFirstPin('')
      setConfirm('')
      return
    }
    const legacyPwd =
      pendingLegacyWalletPassword ?? (needsLegacyPassword ? localPassword : undefined)
    if (needsLegacyPassword && !legacyPwd?.trim()) {
      setError(t('common.error'))
      return
    }
    setBusy(true)
    try {
      await setupPin(b, legacyPwd)
      await unlockWithPin(b)
      setPendingLegacyWalletPassword(null)
      setLocalPassword('')
      navigate('wallet')
    } catch (e) {
      setError(e instanceof Error ? e.message : t('common.error'))
    } finally {
      setBusy(false)
    }
  }

  if (needsLegacyPassword && !passwordGateDone) {
    return (
      <div className="flex flex-col h-full bg-surface pt-safe px-4 pb-8 justify-center">
        <h2 className="text-xl font-semibold text-white text-center mb-2">{t('pin.setup')}</h2>
        <p className="text-sm text-gray-500 text-center mb-6">{t('onboarding.setPassword')}</p>
        <input
          type="password"
          value={localPassword}
          onChange={e => setLocalPassword(e.target.value)}
          className="input-field mb-4"
          autoComplete="current-password"
        />
        {error ? <p className="text-danger text-sm mb-4">{error}</p> : null}
        <button type="button" className="btn-primary w-full" disabled={busy} onClick={() => void verifyPasswordGate()}>
          {t('common.confirm')}
        </button>
      </div>
    )
  }

  return (
    <div className="flex flex-col h-full bg-surface pt-safe px-4 pb-8">
      <div className="flex-1 flex flex-col justify-center">
        <div className="flex justify-center mb-4">
          <Logo size="xl" />
        </div>
        <h2 className="text-xl font-semibold text-white text-center mb-2">
          {step === 'enter' ? t('pin.setup') : t('pin.confirm')}
        </h2>
        <p className="text-sm text-gray-500 text-center mb-8">{t('pin.setupHint')}</p>

        {step === 'enter' ? (
          <PinInput value={firstPin} onChange={onFirstChange} disabled={busy} />
        ) : (
          <PinInput value={confirm} onChange={onConfirmChange} disabled={busy} />
        )}

        {error ? (
          <p className="text-danger text-sm text-center mt-6">{error}</p>
        ) : null}

        {step === 'confirm' && (
          <button
            type="button"
            className="mt-8 text-sm text-gray-400"
            onClick={() => {
              setStep('enter')
              setFirstPin('')
              setConfirm('')
              setError('')
            }}
          >
            {t('common.back')}
          </button>
        )}
      </div>
    </div>
  )
}
